import axios from 'axios';

// Create an axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add authorization token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear storage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Upload Service
export const uploadLeads = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload/leads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const getLeads = async (params = {}) => {
  const response = await api.get('/upload/leads', { params });
  return response.data;
};

export const getUploadedFiles = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.skip) queryParams.append("skip", params.skip);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.upload_type) queryParams.append("upload_type", params.upload_type);
  const response = await api.get("/upload/files", { params });
  return response.data;
};

export const getCsvLeads = async (csvId, params = {}) => {
  const response = await api.get(`/upload/files/${csvId}/leads`, { params });
  return response.data;
};

export const downloadTemplate = async () => {
  try {
    const response = await api.get('/upload/template', {
      responseType: 'blob'
    });
    
    // Create a link to download the file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lead_template.csv'); 
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error downloading template:', error);
    throw error;
  }
};

// CIBIL Service
export const getCibilStatus = async () => {
  const response = await api.get('/cibil/status');
  return response.data;
};

export const getLeadCibil = async (leadId) => {
  const response = await api.get(`/cibil/lead/${leadId}`);
  return response.data;
};

// Auth Service
export const login = async (email, password) => {
  const formData = new FormData();
  // OAuth2 standard uses 'username' field, but we're sending an email
  formData.append('username', email); // Backend will interpret this as email
  formData.append('password', password);
  
  const response = await api.post('/auth/token', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
  
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/users/me');
  return response.data;
};

// Campaign Service
export const createCampaign = async (campaignData) => {
  const response = await api.post('/campaign', campaignData);
  return response.data;
};

export const listCampaigns = async (params = {}) => {
  const response = await api.get('/campaign', { params });
  return response.data;
};

export const getCampaign = async (campaignId) => {
  console.log('Fetching campaign with ID:', campaignId);
  const response = await api.get(`/campaign/${campaignId}`);
  console.log('Campaign data received:', response.data);
  return response.data;
};

export const updateCampaign = async (campaignId, campaignData) => {
  const response = await api.put(`/campaign/${campaignId}`, campaignData);
  return response.data;
};

export const getFilteredLeads = async (campaignId, params = {}) => {
  console.log('Fetching filtered leads for campaign ID:', campaignId);
  const response = await api.get(`/campaign/${campaignId}/leads`, { params });
  console.log('Filtered leads received:', response.data);
  return response.data;
};

export const generateJourneyUrls = async (campaignId) => {
  console.log('Generating journey URLs for campaign ID:', campaignId);
  const response = await api.post(`/campaign/${campaignId}/generate-urls`);
  console.log('Journey URLs generation response:', response.data);
  return response.data;
};

export const downloadLeads = async (campaignId, includeUrls = true) => {
  const response = await api.get(`/campaign/${campaignId}/download`, {
    params: { include_urls: includeUrls },
    responseType: 'blob',
  });
  
  return response.data;
};

// CIBIL Data Upload Service
export const uploadCibilData = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/cibil_upload/cibil-data', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const validateCibilCsv = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/cibil_upload/cibil-data/validate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const getCibilUploadFiles = async (params = {}) => {
  const response = await api.get('/cibil_upload/cibil-data/files', { params });
  return response.data;
};

export const getCibilProcessingProgress = async (csvId) => {
  const response = await api.get(`/cibil_upload/cibil-data/files/${csvId}/progress`);
  return response.data;
};

export const downloadCibilTemplate = async () => {
  try {
    const response = await api.get('/cibil_upload/cibil-data/template', {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'cibil_data_template.csv'); 
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error downloading CIBIL template:', error);
    throw error;
  }
};

export const getCibilDataStats = async () => {
  const response = await api.get('/cibil_upload/cibil-data/stats');
  return response.data;
};

export const createCampaignDynamic = async (campaignData) => {
  const response = await api.post('/campaign/dynamic', campaignData);
  return response.data;
};

export const getAvailableFilters = async () => {
  const response = await api.get('/filters/available');
  return response.data;
};

export const getFilteredLeadsDynamic = async (campaignId, params = {}) => {
  console.log('Fetching dynamic filtered leads for campaign ID:', campaignId);
  const response = await api.get(`/campaign/${campaignId}/leads-dynamic`, { params });
  console.log('Dynamic filtered leads received:', response.data);
  return response.data;
};

export const getCampaignDynamic = async (campaignId) => {
  const response = await api.get(`/campaign/${campaignId}/dynamic`);
  return response.data;
};